import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCurrencyUsd } from '@mdi/js'
import { useLocale } from '../../contexts/LocaleContext'
import { formatAICost, formatTokens } from '../../lib/aiUsage'
import type { ImportAIUsage } from './importTypes'

interface ImportAIUsageStatProps {
    usage: ImportAIUsage
}

/**
 * "What did this deposit cost?" (spec 2026-08-16-08 §3) — a single compact
 * line rather than a card, because it answers one question and competes with
 * nothing on the page.
 *
 * Rendered only when the summary carries `ai_usage`: a blob frozen before the
 * rollup shipped has no figures, and showing $0.00 there would be a confident
 * wrong answer. The dashboard owns that decision (see ImportDashboardPage).
 */
export function ImportAIUsageStat({ usage }: ImportAIUsageStatProps) {
    const { t } = useTranslation('imports')
    const { locale } = useLocale()

    return (
        <section
            data-testid="import-ai-usage"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border bg-card px-4 py-3 text-sm"
        >
            <AppIcon tone="muted" path={mdiCurrencyUsd} size="md" className="shrink-0" />
            <span className="text-muted-foreground">{t('ai_usage.title')}</span>
            <span className="font-semibold text-foreground" data-testid="import-ai-usage-cost">
                {formatAICost(usage.cost_usd, locale)}
            </span>
            <span className="text-muted-foreground" data-testid="import-ai-usage-tokens">
                {t('ai_usage.tokens', {
                    input: formatTokens(usage.input_tokens, locale),
                    output: formatTokens(usage.output_tokens, locale),
                })}
            </span>
        </section>
    )
}
